/**
 * Raw-PII keys blanked out of the Facebook-routed Segment payload once the hashed
 * Advanced Matching traits take their place. `email`, `owner_email`, `business_phone`,
 * `owner_name`, `city`, `postalCode` and `street_address_and_number` (web's name for
 * iOS's `address`) mirror iOS `MetaAdvancedMatchingKeys.all` / `RegistrationUserDataBuilder`
 * (BooksyKit MR !160 + android-biz MR !6420, which added `address` after finding it still
 * leaked post-launch — the exact class of gap this list is meant to close). `latitude`,
 * `longitude` and `shared_location_room_number` have no iOS analogue (mobile does not
 * track precise geo-coordinates as an analytics property) but are blanked too under the
 * same "no raw PII to Facebook" rule — confirmed present raw in a live
 * `Business_Registration_Completed` payload during manual testing. Only `email`/`phone`
 * get a hashed Advanced Matching replacement (this ticket's scope); the rest are blanked
 * with no replacement, same as iOS's plain removal.
 *
 * These are forced to an empty string unconditionally, even when absent from the
 * current event: Segment's identify() calls merge traits into a client-side cache
 * across the whole session, so a key missing here may still hold a raw value cached
 * from an earlier, non-Facebook-routed call. Deleting the key from this call's payload
 * would leave that cached value untouched and still readable by the Facebook
 * destination; overwriting it blanks the cache too. (iOS uses plain removal instead
 * because FBSDKCoreKit does not merge parameters across calls the way Segment's
 * identify() does.)
 */
const RAW_PII_KEYS = [
  'email',
  'business_phone',
  'owner_email',
  'owner_name',
  'city',
  'postalCode',
  'street_address_and_number',
  'shared_location_room_number',
  'latitude',
  'longitude',
] as const

/**
 * Returns a copy of `params` with the raw-PII keys forced to an empty string,
 * preserving everything else.
 */
export function redactRawPii (params: Record<string, unknown>): Record<string, unknown> {
  const redacted = { ...params }

  RAW_PII_KEYS.forEach((key) => {
    redacted[key] = ''
  })

  return redacted
}
